// dependencies
import React from 'react'
import PropTypes from 'prop-types'
import { Grid, makeStyles } from '@material-ui/core'
import classNames from 'classnames'
// helpers
import { mapSpacing, toGridBreakpoint } from '@helpers/strapi'
// context
import { useBannerContext } from '@context/bannerContext'

// components
import BaseBanner from './BaseBanner'
import Button from './BannerButton'
import Image from '../Image'
import Video from '../Video'
import Countdown from '../Countdown'
import Text from '../BannerText'
import ImageWithSku from '../ImageWithSku'
import SignUpForm from '../SignUpForm/SignUpForm'

import { shouldBannerHaveImage } from '.'

const useStyles = makeStyles(() => ({
  contentWrapper: ({ isMosaicGridTile }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    margin: 0,
    '& > div': {
      height: 'auto',
      flexBasis: 'auto',
      ...(isMosaicGridTile ? { flex: 1 } : {}),
    },
  }),
}))

const justify = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
}

const align = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
  expanded: 'space-between',
}

const StrapiContentBanner = ({ Banner, Section, isFullWidth, isMosaicGridTile = false, ...gridProps }) => {
  const classes = useStyles({ isMosaicGridTile })
  const { isHero } = useBannerContext()
  const content = Banner?.Content || []

  const hasImage = shouldBannerHaveImage(Section)
  const direction = Banner?.Orientation || 'row'
  const props = {
    ...gridProps,
    className: classNames(gridProps?.className || '', classes.contentWrapper),
    direction,
    justifyContent: direction === 'row' ? justify[Banner?.Alignment] || null : align[Banner?.VerticalAlignment] || null,
    alignItems: direction === 'row' ? align[Banner?.VerticalAlignment] || null : justify[Banner?.Alignment] || null,
  }

  const ContentComponents = content.map(bannerContent => {
    const BannerData = bannerContent?.BannerText || null
    const ButtonData = bannerContent?.BannerButton || null
    const BannerImage = bannerContent?.BannerImage || null
    const BannerVideo = bannerContent?.Video ? bannerContent : null // need to pass all content to match expected object
    const BaseCountdown = bannerContent?.Countdown || null
    const SignupForm = bannerContent?.SignUpForm || null

    const View = {
      BannerBannerText: BannerData && (
        <Text
          data={{ BannerText: BannerData }}
          Alignment={Banner?.Alignment}
          VerticalAlignment={Banner?.VerticalAlignment}
          key={BannerData?.id}
          isMosaicGridTile={isMosaicGridTile}
        />
      ),
      BannerBannerButton: ButtonData && <Button data={ButtonData} key={ButtonData.id} />,
      BannerBannerImage: BannerImage && (
        <Grid
          item
          md={BannerImage?.ColumnSize || 12}
          style={{
            width: '100%',
          }}
          key={`${BannerImage?.id}-image-grid`}
        >
          {BannerImage.ImageWithSku && <ImageWithSku data={BannerImage.ImageWithSku} />}
          {!BannerImage.ImageWithSku && (
            <Image
              loading={isHero || isMosaicGridTile ? 'eager' : 'lazy'}
              auto
              data={{ mobile: BannerImage?.MobileImage, desktop: BannerImage?.Image }}
              key={BannerImage.id}
              isMosaicGridTile={isMosaicGridTile}
              link={BannerImage?.Link?.Link}
            />
          )}
        </Grid>
      ),

      BannerBannerVideo: BannerVideo && (
        <Video
          data={BannerVideo}
          sx={{ marginTop: { xs: 80, md: 0 } }}
          key={BannerVideo?.id}
          isFullWidth={isFullWidth}
        />
      ),
      BaseCountdown: BaseCountdown && <Countdown data={BaseCountdown} key={BaseCountdown?.id} />,
      PageSignUpForm: SignupForm && <SignUpForm data={{ SignUpForm: SignupForm }} />,
    }
    return View[bannerContent.__typename] || null
  })

  return hasImage ? (
    <BaseBanner
      data={{ Banner: Section }}
      key={`${Banner.id}}-content`}
      sm={toGridBreakpoint(Banner?.ColumnSize)}
      spacing={mapSpacing(Banner?.ContentSpacing)}
      {...props}
    >
      {ContentComponents}
    </BaseBanner>
  ) : (
    <Grid
      key={`${Banner.id}}-content`}
      item
      sm={toGridBreakpoint(Banner?.ColumnSize)}
      container
      spacing={mapSpacing(Banner?.ContentSpacing)}
      {...props}
    >
      {ContentComponents}
    </Grid>
  )
}

StrapiContentBanner.propTypes = {
  Banner: PropTypes.object.isRequired,
  Section: PropTypes.object.isRequired,
  className: PropTypes.string,
  isFullWidth: PropTypes.bool,
  isMosaicGridTile: PropTypes.bool,
}

export default StrapiContentBanner
